// Import modular APIs
import { authApi } from './auth.api';
import { coursesApi } from './courses.api';
import { sandboxApi } from './sandbox.api';
import { usersApi } from './users.api';
import { tutorsApi } from './tutors.api';
import { groupsApi } from './groups.api';
import { repositoriesApi } from './repositories.api';
import { emailApi } from './email.api';
import { subscriptionsApi } from './subscriptions.api';
import { shareApi } from './share.api';
import { ltiApi } from './lti.api';

// Export modular API
export { authApi, coursesApi, sandboxApi, usersApi, tutorsApi, groupsApi, repositoriesApi, emailApi, subscriptionsApi, shareApi, ltiApi };

// Export base api instance for direct use
export { default as api } from './base';

// Legacy function aliases for backward compatibility
// These wrap the new modular API methods with old function names
// TODO: Gradually migrate all components to use modular API directly

// === SANDBOX API LEGACY FUNCTIONS ===
export const startCamundaProcess = (token, prompt, userId, route, sandbox, ai_tutor_id, options = {}) =>
  sandboxApi.startProcess(prompt, userId, route, sandbox, ai_tutor_id, token, options);

export const fetchInstanceByProcessId = (token, processId) =>
  sandboxApi.getInstanceByProcessId(processId, token);

export const updateInstanceArchitecture = (token, instanceId, architecture) =>
  sandboxApi.updateInstanceArchitecture(instanceId, architecture, token);

export const createSandboxComponent = (token, componentData) =>
  sandboxApi.createComponent(componentData, token);

export const deployTemplate = (token, templateId, assignmentId = null) =>
  sandboxApi.deployTemplate(templateId, assignmentId, token);

export const deleteTemplate = (token, templateId) =>
  sandboxApi.deleteTemplate(templateId, token);

export const fetchActiveSandboxes = (token, userId, role = 'all') =>
  sandboxApi.getActiveSandboxes(userId, token);

export const fetchInstances = (token, sandboxId) =>
  sandboxApi.getInstances(sandboxId, token);

export const getProcessStage = (token, processId) =>
  sandboxApi.getProcessStage(processId, token);

export const fetchSandboxComponents = (token, sandboxId) =>
  sandboxApi.getComponents(sandboxId, token);

export const fetchSandboxComponentsRelations = (token, sandboxId, componentIds) =>
  sandboxApi.getComponentsRelations(sandboxId, componentIds, token);

export const executeSQLQuery = (token, componentId, query) =>
  sandboxApi.executeSQLQuery(componentId, query, token);

export const fetchComponentTypes = (token) =>
  sandboxApi.getComponentTypes(token);

export const updateSandboxName = (token, sandboxId, newName) =>
  sandboxApi.updateSandboxName(sandboxId, newName, token);

export const updateSandboxCoworkers = (token, sandboxId, coworkerIds) =>
  sandboxApi.updateSandboxCoworkers(sandboxId, coworkerIds, token);

export const createEnvironmentTemplate = (token, sandboxId) =>
  sandboxApi.createEnvironmentTemplate(sandboxId, token);

export const fetchEnvironmentTemplates = (token) =>
  sandboxApi.getEnvironmentTemplates(token);

export const getAssistantResponse = (token, data) =>
  sandboxApi.getAssistantResponse(data, token);

// === COURSES API LEGACY FUNCTIONS ===
export const fetchCourses = (params = {}) =>
  coursesApi.getAll(params);

export const fetchCourseDetails = (courseId) =>
  coursesApi.getById(courseId);

export const fetchCourseCategories = () =>
  coursesApi.getCategories();

export const createCourse = (token, courseData) =>
  coursesApi.create(courseData, token);

export const updateCourse = (token, courseId, courseData) =>
  coursesApi.update(courseId, courseData, token);

export const deleteCourse = (token, courseId) =>
  coursesApi.delete(courseId, token);

export const enrollInCourse = (token, courseId) =>
  coursesApi.enroll(courseId, token);

export const unenrollFromCourse = (token, courseId) =>
  coursesApi.unenroll(courseId, token);

export const fetchEnrollments = (token) =>
  coursesApi.getEnrollments(token);

export const fetchCreatedCourses = (token) =>
  coursesApi.getCreatedCourses(token);

export const fetchRecommendedCourses = (token) =>
  coursesApi.getRecommendations(token);

// Modules (note: old API didn't require courseId in first parameter)
export const createModule = (token, moduleData) =>
  coursesApi.createModule(moduleData.course, moduleData, token);

export const updateModule = (token, moduleId, moduleData) =>
  coursesApi.updateModule(moduleData.course, moduleId, moduleData, token);

export const deleteModule = (token, moduleId, courseId) =>
  coursesApi.deleteModule(courseId, moduleId, token);

// Lessons
export const fetchLessonDetails = (token, courseId, lessonId) =>
  coursesApi.getLesson(lessonId, token);

export const fetchLessonAssignments = (token, lessonId) =>
  coursesApi.getLessonAssignments(lessonId, token);

export const createLesson = (token, lessonData) =>
  coursesApi.createLesson(lessonData, token);

export const updateLesson = (token, lessonId, lessonData) =>
  coursesApi.updateLesson(lessonId, lessonData, token);

export const deleteLesson = (token, lessonId) =>
  coursesApi.deleteLesson(lessonId, token);

export const completeLesson = (token, lessonId) =>
  coursesApi.completeLesson(lessonId, token);

// Assignments
export const getAssignmentDetails = (token, assignmentId) =>
  coursesApi.getAssignment(assignmentId, token);

export const getAssignmentStatus = (token, assignmentId) =>
  coursesApi.getAssignmentStatus(assignmentId, token);

export const getAssignmentFeedback = (token, assignmentId) =>
  coursesApi.getAssignmentFeedback(assignmentId, token);

export const createAssignment = (token, assignmentData) =>
  coursesApi.createAssignment(assignmentData, token);

export const updateAssignment = (token, assignmentId, assignmentData) =>
  coursesApi.updateAssignment(assignmentId, assignmentData, token);

export const deleteAssignment = (token, assignmentId) =>
  coursesApi.deleteAssignment(assignmentId, token);

export const fetchUserAssignments = (token) =>
  coursesApi.getUserAssignments(token);

export const fetchCreatedAssignments = (token) =>
  coursesApi.getCreatedAssignments(token);

export const submitAssignment = (token, assignmentId, data) =>
  coursesApi.createSubmission(assignmentId, data, token);

export const fetchAssignmentSubmissions = (token, assignmentId) =>
  coursesApi.getAssignmentSubmissions(assignmentId, token);

export const attachSandboxToAssignment = (token, assignmentId, sandboxId, templateId) =>
  coursesApi.attachSandboxToAssignment(assignmentId, sandboxId, templateId, token);

export const attachAssistantToCourse = (token, courseId, tutorId) =>
  coursesApi.attachAssistantToCourse(courseId, tutorId, token);

// Reviews & Comments
export const fetchCourseReviews = (courseId) =>
  coursesApi.getReviews(courseId);

export const submitCourseReview = (token, courseId, reviewData) =>
  coursesApi.createReview(courseId, reviewData, token);

export const fetchCourseComments = (token, courseId) =>
  coursesApi.getComments(courseId, token);

export const createCourseComment = (token, courseId, content) =>
  coursesApi.createComment(courseId, content, token);

export const fetchCourseRatings = (courseId) =>
  coursesApi.getRatings(courseId);

export const getStudentEnvironmentStatus = (token, environmentId) =>
  coursesApi.getStudentEnvironmentStatus(environmentId, token);

// === TUTORS API LEGACY FUNCTIONS ===
export const fetchAITutors = (token, type = 'owned') =>
  tutorsApi.getAITutors(type, token);

export const fetchUserAITutors = (token) =>
  tutorsApi.getAITutors('owned', token);

export const fetchSharedAITutors = (token) =>
  tutorsApi.getAITutors('shared', token);

export const createAITutor = (token, tutorData) =>
  tutorsApi.createAITutor(tutorData, token);

export const fetchAITutorDetails = (token, tutorId) =>
  tutorsApi.getAITutor(tutorId, token);

export const updateAITutor = (token, tutorId, tutorData) =>
  tutorsApi.updateAITutor(tutorId, tutorData, token);

export const updateTutor = (token, tutorId, tutorData) =>
  tutorsApi.updateTutor(tutorId, tutorData, token);

export const deleteAITutor = (token, tutorId) =>
  tutorsApi.deleteAITutor(tutorId, token);

export const createAITutorConversationWithTutor = (token, title, tutorId) =>
  tutorsApi.createConversation(title, tutorId, token);

export const fetchAITutorConversations = (token) =>
  tutorsApi.getConversations(token);

export const fetchAITutorConversationsByTutor = (token, tutorId) =>
  tutorsApi.getConversations(token);

export const fetchChatHistory = (token, conversationId) =>
  tutorsApi.getChatHistory(conversationId, token);

export const sendMessageToAITutor = (token, tutorId, data) =>
  tutorsApi.sendMessage(tutorId, data, token);

export const uploadAITutorMaterial = (token, tutorId, file) =>
  tutorsApi.uploadMaterial(tutorId, file, token);

export const fetchSandboxesForTutor = (token) =>
  tutorsApi.getSandboxes(token);

export const fetchSandboxInstancesForTutor = (token) =>
  tutorsApi.getSandboxInstances(token);

// === USERS API LEGACY FUNCTIONS ===
export const fetchUserProfile = (token, userId) =>
  usersApi.getProfile(userId, token);

export const updateUserProfile = (token, profileId, profileData) =>
  usersApi.updateProfile(profileId, profileData, token);

export const searchUsers = (token, searchTerm) =>
  usersApi.searchUsers(searchTerm, token);

export const fetchUserAchievements = (token) =>
  usersApi.getAchievements(token);

export const fetchUserBadges = (token) =>
  usersApi.getBadges(token);

// === GROUPS API LEGACY FUNCTIONS ===
export const fetchUserGroups = (token) =>
  groupsApi.getAll(token);

export const fetchGroupDetails = (token, groupId) =>
  groupsApi.getById(groupId, token);

export const createGroup = (token, groupData) =>
  groupsApi.create(groupData, token);

export const updateGroup = (token, groupId, groupData) =>
  groupsApi.update(groupId, groupData, token);

export const deleteGroup = (token, groupId) =>
  groupsApi.delete(groupId, token);

export const addUserToGroup = (token, groupId, userId) =>
  groupsApi.addMember(groupId, userId, token);

export const removeUserFromGroup = (token, groupId, userId) =>
  groupsApi.removeMember(groupId, userId, token);

export const searchGroups = (token, searchTerm) =>
  usersApi.searchGroups(searchTerm, token);

export const createUserAssignment = (token, assignmentData) =>
  coursesApi.createAssignment(assignmentData, token);

// === REPOSITORIES API LEGACY FUNCTIONS ===
export const fetchMaterialRepositories = (token, tutorId = null) =>
  repositoriesApi.getMaterialRepositories(token);

export const createMaterialRepository = (token, data) =>
  repositoriesApi.createMaterialRepository(data, token);

export const deleteMaterialRepository = (token, repositoryId) =>
  repositoriesApi.deleteMaterialRepository(repositoryId, token);

export const linkRepositoryToTutor = (token, tutorId, repositoryId) =>
  repositoriesApi.linkRepositoryToTutor(tutorId, repositoryId, token);

export const unlinkRepositoryFromTutor = (token, tutorId, repositoryId) =>
  repositoriesApi.unlinkRepositoryFromTutor(tutorId, repositoryId, token);

// === SHARE API LEGACY FUNCTIONS ===
export const getTemplateAccessList = (token, templateId) =>
  shareApi.getTemplateAccessList(templateId, token);

export const addTemplateAccess = (templateId, userIds = [], groupIds = [], token) =>
  shareApi.addTemplateAccess(templateId, userIds, groupIds, token);

export const removeTemplateAccess = (templateId, accessId, token) =>
  shareApi.removeTemplateAccess(templateId, accessId, token);

export const getTutorAccessList = (token, tutorId) =>
  shareApi.getTutorAccessList(tutorId, token);

export const addTutorAccess = (token, tutorId, userIds = [], groupIds = []) =>
  shareApi.addTutorAccess(tutorId, userIds, groupIds, token);

export const removeTutorAccess = (token, tutorId, accessId) =>
  shareApi.removeTutorAccess(accessId, token);

export const getAccessList = (token, courseId) =>
  shareApi.getCourseAccessList(courseId, token);

export const addAccess = (token, courseId, userId, groupId) =>
  shareApi.addCourseAccess(courseId, userId, groupId, token);

export const removeAccess = (token, accessId) =>
  shareApi.removeCourseAccess(accessId, token);

// === SUBSCRIPTIONS API LEGACY FUNCTIONS ===
export const fetchSubscriptionStatus = (token) =>
  subscriptionsApi.getStatus(token);

// === API CLIENTS LEGACY FUNCTIONS ===
export const fetchApiClients = (token) =>
  usersApi.getApiClients(token);

export const createApiClient = (token, name) =>
  usersApi.createApiClient(name, token);

export const deleteApiClient = (token, clientId) =>
  usersApi.deleteApiClient(clientId, token);

export const regenerateApiClientSecret = (token, clientId) =>
  usersApi.regenerateApiClientKey(clientId, token);
